/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { merge } from '@adobe-commerce/elsie/lib';
import {
  CartModel,
  StoreConfigModel,
  type AvailableFreeGiftRule,
  type FreeGiftBundleItem,
  type FreeGiftProduct,
} from '@/cart/data/models';
import { state } from '@/cart/lib/state';
import { config } from '@/cart/api';

function transformFreeGiftBundleItems(rawItems: any[]): FreeGiftBundleItem[] {
  const rows: FreeGiftBundleItem[] = [];
  for (const item of rawItems ?? []) {
    const uid = String(item?.uid ?? '');
    if (!uid) continue;

    const values = (item?.options ?? [])
      .filter(Boolean)
      .map((opt: any) => ({
        uid: String(opt.uid ?? ''),
        label: String(
          opt.label ?? opt.product?.name ?? opt.product?.sku ?? ''
        ),
        isDefault: Boolean(opt.is_default),
      }))
      .filter((v: { uid: string }) => v.uid.length > 0);

    if (!values.length) continue;

    rows.push({
      uid,
      title: String(item.title ?? ''),
      required: Boolean(item.required),
      position: typeof item.position === 'number' ? item.position : undefined,
      values,
    });
  }
  return rows;
}

function transformFreeGiftProduct(raw: any): FreeGiftProduct {
  const configurableOptions = raw?.configurable_options?.length
    ? raw.configurable_options.map((co: any) => ({
        uid: co.uid,
        attributeUid: co.attribute_uid ?? co.uid ?? '',
        label: co.label ?? '',
        values: (co.values ?? []).map((v: any) => ({
          uid: v.uid ?? '',
          label: v.label ?? '',
        })),
      }))
    : undefined;

  const bundleItems =
    raw?.items?.length && Array.isArray(raw.items)
      ? transformFreeGiftBundleItems(raw.items)
      : undefined;

  return {
    sku: raw?.sku ?? '',
    parentSku: raw?.parent_sku ?? raw?.parentSku ?? undefined,
    name: raw?.name ?? raw?.sku ?? '',
    typeName: raw?.__typename,
    thumbnail: raw?.thumbnail
      ? { url: raw.thumbnail.url, label: raw.thumbnail.label }
      : null,
    configurableOptions,
    bundleItems: bundleItems?.length ? bundleItems : undefined,
  };
}

export function transformAvailableFreeGifts(
  rules: any[] | null | undefined
): AvailableFreeGiftRule[] {
  if (!rules?.length) return [];

  return rules.map((rule: any) => ({
    ruleId: rule.rule_id,
    ruleLabel:
      rule.rule_label != null && String(rule.rule_label).trim() !== ''
        ? String(rule.rule_label).trim()
        : undefined,
    giftQty: rule.gift_qty ?? 1,
    availableSkus: rule.available_skus ?? [],
    products: (rule.products ?? []).map(transformFreeGiftProduct),
  }));
}

/** Supports Commerce `is_salable` and legacy mesh fields that still expose `is_available`. */
export function resolveIsSalable(item: {
  is_salable?: boolean | null;
  is_available?: boolean | null;
}): boolean {
  if (typeof item.is_salable === 'boolean') return item.is_salable;
  if (typeof item.is_available === 'boolean') return item.is_available;
  return true;
}

export function transformCart(data: any): CartModel | null {
  if (!data) return null;

  const model = {
    appliedGiftCards:
      data?.applied_gift_cards?.map((giftCard: any) => {
        const giftCartState = {
          code: giftCard.code ?? '',
          appliedBalance: {
            value: giftCard.applied_balance.value ?? 0,
            currency: giftCard.applied_balance.currency ?? 'USD',
          },
          currentBalance: {
            value: giftCard.current_balance.value ?? 0,
            currency: giftCard.current_balance.currency ?? 'USD',
          },
          expirationDate: giftCard.expiration_date ?? '',
        };

        const currentBalanceValue = giftCartState?.currentBalance?.value;
        const appliedBalanceValue = giftCartState?.appliedBalance?.value;
        const currency = giftCartState?.currentBalance?.currency;

        const hasBalanceDifference =
          currentBalanceValue - appliedBalanceValue > 0;

        const value = hasBalanceDifference
          ? currentBalanceValue - appliedBalanceValue
          : 0;

        return {
          ...giftCartState,
          giftCardBalance: { value, currency },
        };
      }) ?? [],
    id: data.id,
    totalQuantity: transformQuantity(data),
    totalUniqueItems: data.itemsV2.items.length,
    totalGiftOptions: transformGiftOptions(data?.prices?.gift_options),
    giftReceiptIncluded: data?.gift_receipt_included ?? false,
    printedCardIncluded: data?.printed_card_included ?? false,
    cartGiftWrapping:
      data?.available_gift_wrappings?.map((wrapItem: any) => {
        return {
          design: wrapItem.design ?? '',
          uid: wrapItem.uid,
          selected: data?.gift_wrapping?.uid === wrapItem.uid,
          image: {
            url: wrapItem?.image?.url ?? '',
            label: wrapItem.image?.label ?? '',
          },
          price: {
            currency: wrapItem?.price?.currency ?? 'USD',
            value: wrapItem?.price?.value ?? 0,
          },
        };
      }) ?? [],
    giftMessage: {
      senderName: data?.gift_message?.from ?? '',
      recipientName: data?.gift_message?.to ?? '',
      message: data?.gift_message?.message ?? '',
    },
    errors: transformErrors(data?.itemsV2),
    items: transformItems(data?.itemsV2),
    miniCartMaxItems: transformItems(data?.itemsV2).slice(
      0,
      state.config?.miniCartMaxItemsDisplay ?? 10
    ),
    total: {
      includingTax: {
        value: data.prices.grand_total.value,
        currency: data.prices.grand_total.currency,
      },
      excludingTax: {
        value: data.prices.grand_total_excluding_tax.value,
        currency: data.prices.grand_total_excluding_tax.currency,
      },
    },
    discount: calculateTotal(
      data.prices.discounts,
      data.prices.grand_total.currency
    ),
    subtotal: {
      excludingTax: {
        value: data.prices.subtotal_excluding_tax?.value,
        currency: data.prices.subtotal_excluding_tax?.currency,
      },
      includingTax: {
        value: data.prices.subtotal_including_tax?.value,
        currency: data.prices.subtotal_including_tax?.currency,
      },
      includingDiscountOnly: {
        value: data.prices.subtotal_with_discount_excluding_tax?.value,
        currency: data.prices.subtotal_with_discount_excluding_tax?.currency,
      },
    },
    appliedTaxes: transformTotalPriceModifiers(data.prices.applied_taxes),
    totalTax: calculateTotal(
      data.prices.applied_taxes,
      data.prices.grand_total.currency
    ),
    appliedDiscounts: transformTotalPriceModifiers(data.prices.discounts),
    isVirtual: data.is_virtual,
    addresses: {
      shipping: data.shipping_addresses && transformAddresses(data),
    },
    isGuestCart: !state.authenticated,
    hasOutOfStockItems: transformOutOfStockItems(data),
    hasFullyOutOfStockItems: transformFullyOutOfStockItems(data),
    appliedCoupons: data.applied_coupons,
    hasAvailableFreeGifts: Boolean(data?.has_available_free_gifts),
    availableFreeGifts: transformAvailableFreeGifts(data?.available_free_gifts),
  };

  // extend the model with custom transformer
  return merge(
    model,
    config.getConfig().models?.CartModel?.transformer?.(data)
  );
}

function transformGiftOptions(giftOptions: any) {
  return {
    giftWrappingForItems: {
      value: giftOptions?.gift_wrapping_for_items?.value ?? 0,
      currency: giftOptions?.gift_wrapping_for_items?.currency ?? 'USD',
    },
    giftWrappingForItemsInclTax: {
      value: giftOptions?.gift_wrapping_for_items_incl_tax?.value ?? 0,
      currency:
        giftOptions?.gift_wrapping_for_items_incl_tax?.currency ?? 'USD',
    },
    giftWrappingForOrder: {
      value: giftOptions?.gift_wrapping_for_order?.value ?? 0,
      currency: giftOptions?.gift_wrapping_for_order?.currency ?? 'USD',
    },
    giftWrappingForOrderInclTax: {
      value: giftOptions?.gift_wrapping_for_order_incl_tax?.value ?? 0,
      currency:
        giftOptions?.gift_wrapping_for_order_incl_tax?.currency ?? 'USD',
    },
    printedCard: {
      value: giftOptions?.printed_card?.value ?? 0,
      currency: giftOptions?.printed_card?.currency ?? 'USD',
    },
    printedCardInclTax: {
      value: giftOptions?.printed_card_incl_tax?.value ?? 0,
      currency: giftOptions?.printed_card_incl_tax?.currency ?? 'USD',
    },
  };
}

// Need to calculate total amount since it's not provided by GraphQL
function calculateTotal(data: any[], currency: string) {
  if (!data?.length)
    return {
      value: 0,
      currency,
    };

  return data.reduce(
    (acc: any, item: any) => {
      return {
        value: acc.value + item.amount.value,
        currency: item.amount.currency,
      };
    },
    { value: 0, currency }
  );
}

function getImage(config: StoreConfigModel | null | undefined, item: any) {
  return {
    // Use parent thumbnail if configured, otherwise use own variant. use the parent thumbnail as a fallback
    src: config?.useConfigurableParentThumbnail
      ? item.product.thumbnail.url
      : item.configured_variant?.thumbnail?.url || item.product.thumbnail.url,
    alt: config?.useConfigurableParentThumbnail
      ? item.product.thumbnail.label
      : item.configured_variant?.thumbnail?.label ||
        item.product.thumbnail.label,
  };
}

function getRegularPrice(item: any) {
  if (item.__typename === 'ConfigurableCartItem') {
    return {
      value:
        item.configured_variant?.price_range?.maximum_price.regular_price.value,
      currency:
        item.configured_variant?.price_range?.maximum_price.regular_price
          .currency,
    };
  }
  if (item.__typename === 'GiftCardCartItem') {
    return {
      value: item.prices.price.value,
      currency: item.prices.price.currency,
    };
  }
  return {
    value: item.prices.original_item_price.value,
    currency: item.prices.original_item_price.currency,
  };
}

/**
 * Finds the most applicable price tier for a given quantity
 * @param priceTiers - Array of price tier objects
 * @param quantity - Current item quantity
 * @returns The applicable tier or null if none found
 */
function findApplicablePriceTier(priceTiers: any[], quantity: number) {
  if (!priceTiers?.length) return null;

  // Sort price tiers by quantity in descending order to find the highest applicable tier
  const sortedTiers = [...priceTiers].sort(
    (a: any, b: any) => b.quantity - a.quantity
  );

  // Find the highest tier where the current quantity meets or exceeds the tier's minimum quantity
  return sortedTiers.find((tier: any) => quantity >= tier.quantity) || null;
}

function getDiscounted(item: any) {
  const quantity = item.quantity;
  const isConfigurable = item.__typename === 'ConfigurableCartItem';

  // Get price tiers and price range based on item type
  const priceTiers = isConfigurable
    ? item.configured_variant?.price_tiers
    : item.product.price_tiers;
  const priceRange = isConfigurable
    ? item.configured_variant?.price_range
    : item.product.price_range;

  // Check price tiers first for discount eligibility
  const applicableTier = findApplicablePriceTier(priceTiers, quantity);
  if (applicableTier) {
    return applicableTier.discount.amount_off > 0;
  }

  // Fallback to regular price range discount
  return priceRange?.maximum_price.discount.amount_off > 0;
}

function transformGiftMessage(data: any) {
  return {
    senderName: data?.gift_message?.from ?? '',
    recipientName: data?.gift_message?.to ?? '',
    message: data?.gift_message?.message ?? '',
  };
}

function transformWrappingPrice(data: any) {
  return {
    currency: data?.currency ?? 'USD',
    value: data?.value ?? 0,
  };
}

/**
 * Line-level discount rows: prefer `product.discounts` when the backend exposes it
 * (e.g. mesh); otherwise Magento cart items only provide `prices.discounts`.
 */
function getFreeGiftDiscountEntries(item: any): any[] | undefined {
  const fromProduct = item?.product?.discounts;
  if (Array.isArray(fromProduct) && fromProduct.length > 0) {
    return fromProduct;
  }
  const fromPrices = item?.prices?.discounts;
  if (Array.isArray(fromPrices) && fromPrices.length > 0) {
    return fromPrices;
  }
  return undefined;
}

/** True when the API exposes line-level discount rows (labels/amounts for display). */
function isFreeGiftNetPriceDisplay(item: any): boolean {
  if (!item?.is_free_gift) return false;
  const entries = getFreeGiftDiscountEntries(item);
  return Array.isArray(entries) && entries.length > 0;
}

/**
 * Free gifts: when there are no `discounts` rows, force $0 (legacy display).
 * When discount rows exist, **unit** prices (`pricingDimension: 'unit'`) stay as Magento returns
 * (strike-through + $0 net in the UI). **Line** totals (`'line'`) use Magento `row_total` when
 * it already reflects a partial/net line; when `row_total` still equals `original_row_total`
 * (catalog list row while cart rule makes the line free — Sales Rule “price” mode), normalize to
 * $0 so row totals match cart grand total.
 */
function cartItemMoney(
  item: any,
  money: { value?: number; currency?: string },
  pricingDimension: 'unit' | 'line' = 'unit'
) {
  const currency = money.currency ?? 'USD';

  if (!item?.is_free_gift) {
    return { value: money.value, currency: money.currency ?? 'USD' };
  }

  if (isFreeGiftNetPriceDisplay(item)) {
    if (pricingDimension === 'unit') {
      return { value: Number(money.value ?? 0), currency };
    }
    const rowVal = Number(money.value ?? 0);
    const origRowVal = Number(item.prices?.original_row_total?.value ?? NaN);
    if (Number.isFinite(origRowVal) && rowVal === origRowVal) {
      return { value: 0, currency };
    }
    return { value: rowVal, currency };
  }

  return { value: 0, currency };
}

function transformItems(data: any) {
  if (!data?.items?.length) return [];

  const config = state.config;

  return data.items.map((item: any) => {
    const freeGiftListRowStuck =
      Boolean(item?.is_free_gift) &&
      isFreeGiftNetPriceDisplay(item) &&
      Number(item.prices?.row_total?.value ?? NaN) ===
        Number(item.prices?.original_row_total?.value ?? NaN);

    const lineRow = cartItemMoney(item, item.prices.row_total, 'line');
    const lineRowIncl = freeGiftListRowStuck
      ? {
          value: 0,
          currency:
            item.prices.row_total_including_tax?.currency ??
            item.prices.row_total?.currency ??
            'USD',
        }
      : cartItemMoney(item, item.prices.row_total_including_tax, 'line');
    const isSalable = resolveIsSalable(item);
    const hasBackorderNotice = Boolean(item.backorder_message?.trim());

    return {
    giftWrappingAvailable: item?.product?.gift_wrapping_available ?? false,
    giftWrappingPrice: transformWrappingPrice(
      item?.product?.gift_wrapping_price
    ),
    giftMessage: transformGiftMessage(item),
    productGiftWrapping:
      item?.available_gift_wrapping?.map((wrapItem: any) => {
        return {
          design: wrapItem.design ?? '',
          uid: wrapItem.uid,
          selected: item.gift_wrapping?.uid === wrapItem.uid,
          image: {
            url: wrapItem?.image?.url ?? '',
            label: wrapItem.image?.label ?? '',
          },
          price: {
            currency: wrapItem?.price?.currency ?? 'USD',
            value: wrapItem?.price?.value ?? 0,
          },
        };
      }) ?? [],
    itemType: item.__typename,
    uid: item.uid,
    giftMessageAvailable: transformGiftOptionsCode(
      item.product.gift_message_available
    ),
    url: {
      urlKey: item.product.url_key,
      categories: item.product.categories.map(
        (category: any) => category.url_key
      ),
    },
    canonicalUrl: item.product.canonical_url,
    categories: item.product.categories.map((category: any) => category.name),
    priceTiers:
      item.__typename === 'ConfigurableCartItem'
        ? item.configured_variant?.price_tiers?.map(
            (priceTier: any) => priceTier
          ) || []
        : item.product.price_tiers?.map((priceTier: any) => priceTier) || [],
    quantity: item.quantity,
    sku: transformSKU(item),
    topLevelSku: item.product.sku,
    name: item.product.name,
    image: getImage(config, item),
    price: cartItemMoney(item, item.prices.price, 'unit'),
    taxedPrice: cartItemMoney(item, item.prices.price_including_tax, 'unit'),
    fixedProductTaxes: item.prices.fixed_product_taxes,
    rowTotal: lineRow,
    rowTotalIncludingTax: lineRowIncl,
    links: transformLinks(item.links),
    total: {
      value: item.prices.original_row_total?.value,
      currency: item.prices.original_row_total?.currency,
    },
    discount: {
      value: item.prices.total_item_discount.value,
      currency: item.prices.total_item_discount.currency,
      label: item.prices.discounts?.map((discount: any) => discount.label),
    },
    regularPrice: getRegularPrice(item),
    /** Raw `prices.original_item_price` — catalog special price, unaffected by free-gift zeroing. */
    originalItemPrice: {
      value: item.prices.original_item_price?.value,
      currency: item.prices.original_item_price?.currency,
    },
    discounted: getDiscounted(item),
    isFreeGift: Boolean(item.is_free_gift),
    bundleOptions:
      item.__typename === 'BundleCartItem'
        ? transformBundleOptions(item.bundle_options)
        : null,
    bundleOptionsUIDs:
      item.__typename === 'BundleCartItem'
        ? extractBundleOptionUIDs(item.bundle_options)
        : null,
    selectedOptions: transformSelectedOptions(item.configurable_options)
      ?.options,
    selectedOptionsUIDs: transformSelectedOptions(item.configurable_options)
      ?.uids,
    customizableOptions: transformCustomizableOptions(
      item.customizable_options
    ),
    customizableOptionEntries: transformCustomizableOptionEntries(
      item.customizable_options
    ),
    sender: item.__typename === 'GiftCardCartItem' ? item.sender_name : null,
    senderEmail:
      item.__typename === 'GiftCardCartItem' ? item.sender_email : null,
    recipient:
      item.__typename === 'GiftCardCartItem' ? item.recipient_name : null,
    recipientEmail:
      item.__typename === 'GiftCardCartItem' ? item.recipient_email : null,
    message: item.__typename === 'GiftCardCartItem' ? item.message : null,

    discountedTotal: {
      value: lineRow.value,
      currency: lineRow.currency,
    },

    onlyXLeftInStock:
      item.__typename === 'ConfigurableCartItem'
        ? item.configured_variant?.only_x_left_in_stock
        : item.product.only_x_left_in_stock,
    lowInventory:
      isSalable && item.product.only_x_left_in_stock != null,
    insufficientQuantity:
      !hasBackorderNotice &&
      (item.__typename === 'ConfigurableCartItem'
        ? item.configured_variant
        : item.product
      ).stock_status === 'IN_STOCK' &&
      !isSalable,
    outOfStock:
      !hasBackorderNotice &&
      item.product.stock_status === 'OUT_OF_STOCK' &&
      !isSalable,
    ...(item.backorder_message?.trim()
      ? { backorderMessage: item.backorder_message.trim() }
      : {}),
    stockLevel: transformStockLevel(item),
    // Add support for BundleCartItem
    discountPercentage: transformDiscountPercentage(item),
    savingsAmount: transformDiscountAmount(item),
    productAttributes: transformProductAttributes(item),
  };
  });
}

// This helper follows the approach used in the store configuration GraphQL schema for Gift Messages,
// where a `null` value represents the "use system value" option. It requires special processing
// based on both global and product-level configurations
function transformGiftOptionsCode(data: string) {
  switch (+data) {
    case 0:
      return false;
    case 1:
      return true;
    case 2:
      return null;
    default:
      return false;
  }
}

function transformErrors(data: any) {
  const result = data?.items?.reduce((acc: any, item: any) => {
    item.errors?.forEach((error: Error) => {
      acc.push({ uid: item.uid, text: error.message });
    });

    return acc;
  }, []);

  return result?.length ? result : null;
}

function transformTotalPriceModifiers(data: any) {
  if (!data?.length) return [];

  return data.map((item: any) => ({
    amount: {
      value: item.amount.value,
      currency: item.amount.currency,
    },
    label: item.label,
    coupon: item.coupon,
  }));
}

function transformBundleOptions(data: any) {
  const bundle = data?.map((option: any) => ({
    uid: option.uid,
    label: option.label,
    value: option.values.map((value: any) => value.label).join(', '),
  }));

  const bundleOptions: { [key: string]: any } = {};

  bundle?.forEach((option: any) => {
    bundleOptions[option.label] = option.value;
  });

  return Object.keys(bundleOptions).length > 0 ? bundleOptions : null;
}

function extractBundleOptionUIDs(data: any) {
  if (!data?.length) return null;

  const allUIDs: string[] = [];
  data.forEach((option: any) => {
    if (option.values?.length) {
      const valueUIDs = option.values.map((value: any) => value.uid);
      allUIDs.push(...valueUIDs);
    }
  });

  return allUIDs.length > 0 ? allUIDs : null;
}

function transformSelectedOptions(data: any) {
  const selected = data?.map((option: any) => ({
    uid: option.configurable_product_option_uid,
    label: option.option_label,
    value: option.value_label,
    valueUid: option.configurable_product_option_value_uid,
  }));

  const selectedOptions: { [key: string]: any } = {};
  const selectedOptionsUIDs: { [key: string]: any } = {};

  selected?.forEach((option: any) => {
    selectedOptions[option.label] = option.value;
    selectedOptionsUIDs[option.label] = option.valueUid;
  });

  return {
    options: Object.keys(selectedOptions).length > 0 ? selectedOptions : null,
    uids:
      Object.keys(selectedOptionsUIDs).length > 0 ? selectedOptionsUIDs : null,
  };
}

function transformCustomizableOptionEntries(data: any) {
  if (!data?.length) return undefined;

  return data.map((option: any) => ({
    uid: option.customizable_option_uid,
    label: option.label,
    type: option.type,
    values: (option.values ?? []).map((value: any) => ({
      uid: value.customizable_option_value_uid,
      label: value.label,
      value: value.value,
    })),
  }));
}

function transformCustomizableOptions(data: any) {
  const transformedOptions = data?.map((option: any) => ({
    uid: option.customizable_option_uid,
    label: option.label,
    type: option.type,
    values: option.values.map((value: any) => ({
      uid: value.customizable_option_value_uid,
      label: value.label,
      value: value.value,
    })),
  }));

  const result: { [key: string]: any } = {};

  transformedOptions?.forEach((option: any) => {
    switch (option.type) {
      case 'field':
      case 'area':
      case 'date_time':
        result[option.label] = option.values[0].value;
        break;
      case 'radio':
      case 'drop_down':
        result[option.label] = option.values[0].label;
        break;
      case 'multiple':
      case 'checkbox':
        result[option.label] = (option.values as Array<any>).reduce(
          (previous, current) => {
            if (previous) return `${previous}, ${current.label}`;
            return current.label;
          },
          ''
        );
        break;
      case 'file': {
        const parser = new DOMParser();
        const fileAnchorElementHtml = option.values[0].value;
        const doc = parser.parseFromString(fileAnchorElementHtml, 'text/html');
        const anchorText = doc.querySelector('a')?.textContent || '';
        result[option.label] = anchorText;
        break;
      }
    }
  });
  return result;
}

function transformQuantity(data: any) {
  if (state.config?.cartSummaryDisplayTotal === 0)
    return data.itemsV2.items.length;
  if (state.config?.cartSummaryDisplayTotal === 1) return data.total_quantity;
  // Default to items count if unexpected config value or error returned
  return data.itemsV2.items.length;
}

function transformLinks(links: any) {
  return links?.length > 0
    ? {
        count: links.length,
        result: links.map((link: any) => link.title).join(', '),
      }
    : null;
}

function transformAddresses(data: any) {
  //Shipping Address bound to cart
  if (data.shipping_addresses?.length) {
    return data.shipping_addresses?.map((address: any) => ({
      countryCode: address.country.code,
      zipCode: address.postcode,
      regionCode: address.region.code,
    }));
    //Shipping Address bound to customer
  } else if (data.addresses?.length) {
    return data.addresses
      ?.filter((address: any) => address.default_shipping)
      .map(
        (address: any) =>
          address.default_shipping && {
            countryCode: address.country_code,
            zipCode: address.postcode,
            regionCode: address.region?.region_code,
          }
      );
  }

  return null;
}

/**
 * True when the cart has stock-related issues that need shopper action.
 * Excludes salable backorder lines: `is_salable` already reflects backorder eligibility while
 * `backorder_message` is set, and catalog `stock_status` may still be IN_STOCK.
 */
function transformOutOfStockItems(data: any) {
  return data?.itemsV2?.items?.some((item: any) => {
    if (item.backorder_message?.trim()) {
      return false;
    }
    return !resolveIsSalable(item);
  });
}

function transformStockLevel(item: any) {
  if (!item.not_available_message) return null;

  return item.product.quantity != null ? item.product.quantity : 'noNumber';
}

/** Fully OOS only for lines that are not salable (excludes backorder notices). */
function transformFullyOutOfStockItems(data: any) {
  return data?.itemsV2?.items?.some(
    (item: any) =>
      !item.backorder_message?.trim() &&
      !resolveIsSalable(item) &&
      item?.product?.stock_status === 'OUT_OF_STOCK'
  );
}

// Add support for BundleCartItem in USF-1591 after LYNX-599 is completed
function transformDiscountPercentage(data: any) {
  const quantity = data.quantity;

  // Check price tiers first for discount percentage
  const applicableTier = findApplicablePriceTier(
    data.product.price_tiers,
    quantity
  );

  if (applicableTier) {
    return Math.round(applicableTier.discount.percent_off);
  }

  // Fallback to product-specific discount percentage
  let percent_off;

  if (data.__typename === 'ConfigurableCartItem') {
    percent_off =
      data?.configured_variant?.price_range?.maximum_price?.discount
        ?.percent_off;
  } else if (data.__typename === 'BundleCartItem') {
    return undefined;
  } else {
    percent_off =
      data?.product?.price_range?.maximum_price?.discount?.percent_off;
  }

  if (percent_off === 0) {
    return undefined;
  }

  return Math.round(percent_off);
}

// Add support for returning configured_variant sku for __typename:"ConfigurableCartItem"
function transformSKU(item: any) {
  if (item.__typename === 'ConfigurableCartItem') {
    return item.configured_variant.sku;
  }

  // If varaintSku is available return it, otherwise return product sku
  return item.product?.variantSku || item.product.sku;
}

function transformDiscountAmount(data: any) {
  const amount_off =
    data?.prices?.original_row_total?.value - data?.prices?.row_total?.value;
  const currency = data?.prices?.row_total?.currency;

  if (amount_off === 0) {
    return undefined;
  }
  return {
    value: amount_off,
    currency,
  };
}

function transformProductAttributes(item: any) {
  return item?.product?.custom_attributesV2?.items?.map((attribute: any) => {
    const transformedCode = attribute.code
      .split('_')
      .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return {
      ...attribute,
      code: transformedCode,
    };
  });
}
